import { makeCygnusApiCall } from "../../../../../api/api";

/**
 * Uploads document files for a client.
 *
 * @param clientMasterId - The ID of the client.
 * @param documentMetaData - Metadata for the documents being uploaded.
 * @param documentFiles - Array of files to upload.
 * @param setDocumentUploadApiProgress - State setter to indicate upload progress.
 * @returns The API response if successful.
 */
export const uploadDocumentForClient = async (
  clientMasterId: number,
  documentMetaData: any[],
  documentFiles: any[],
  setDocumentUploadApiProgress: (arg: boolean) => void
) => {
  setDocumentUploadApiProgress(true);
  const formData = new FormData();

  formData.append("ClientMasterId", clientMasterId.toString());
  formData.append("DocumentMetadata", JSON.stringify(documentMetaData));

  documentFiles.forEach((file) => {
    formData.append("DocumentFiles", file); // Keep same key for multiple files
  });

  const response = await makeCygnusApiCall({
    type: "POST",
    payload: formData,
    url: `/client_documents/`,
    show_message: true,
    customized_success_message: "Uploaded document.",
    customized_failure_message:
      "Unable to upload your document, please try again. If the problem persists, please contact support and attach a screenshot.",
    customized_error_message:
      "Error occured while uploading your document, please contact support and attach a screenshot.",
    payload_is_file: true,
    isblob: false,
    forMDM: true,
  });

  setDocumentUploadApiProgress(false);
  return response?.success ? response : undefined;
};

/**
 * Deletes a specific document associated with a client.
 *
 * @param clientDocumentId - The ID of the document to delete.
 * @returns The API response if successful.
 */
export const deleteDocumentForClient = async (
  clientDocumentId: number
) => {
  const response = await makeCygnusApiCall({
    type: "DELETE",
    payload: {
      ClientDocumentId: clientDocumentId,
    },
    url: `/client_documents/`,
    show_message: true,
    customized_success_message: "Deleted document.",
    customized_failure_message:
      "Unable to delete your document, please try again. If the problem persists, please contact support and attach a screenshot.",
    customized_error_message:
      "Error occured while deleting your document, please contact support and attach a screenshot.",
    isblob: false,
    forMDM: true,
  });

  return response?.success ? response : undefined;
};

/**
 * Fetches documents for a given client, optionally filtering by type or ID.
 *
 * @param clientMasterId - The ID of the client.
 * @param setDocumentFetchApiProgress - State setter to indicate loading progress.
 * @param referenceDataCode - (Optional) Document type code for filtering.
 * @param clientDocumentId - (Optional) Specific client document ID to fetch.
 * @returns The fetched document data if successful.
 */
export const fetchDocumentForClient = async (
  clientMasterId: number,
  setDocumentFetchApiProgress: (arg: boolean) => void,
  referenceDataCode?: string,
  clientDocumentId?: number
) => {
  setDocumentFetchApiProgress(true);

  const response = await makeCygnusApiCall({
    type: "GET",
    payload: {},
    url: `/client_documents/?ClientMasterId=${clientMasterId}&ClientDocumentId=${clientDocumentId ?? ""}&DocumentTypeCode=${referenceDataCode ?? ""}`,
    show_message: true,
    customized_success_message: "Fetched document.",
    customized_failure_message:
      "Unable to fetch your document, please try again. If the problem persists, please contact support and attach a screenshot.",
    customized_error_message:
      "Error occured while fetching your document, please contact support and attach a screenshot.",
    isblob: false,
    forMDM: true,
  });

  setDocumentFetchApiProgress(false);
  return response?.success ? response.data : undefined;
};

/**
 * Fetches available document types for background check uploads.
 *
 * @param setFileInputs - Sets the file input types in the component state.
 * @param setDocumentTypesApiProgress - Toggles the loading indicator.
 */
export const fetchDocumentTypes = async (
  setFileInputs: (arg: any) => void,
  setDocumentTypesApiProgress: (arg: boolean) => void,
  isPublic: boolean
) => {
  setDocumentTypesApiProgress(true);

  const response = await makeCygnusApiCall({
    type: "GET",
    payload: {},
    url: `/client_document_types`,
    show_message: true,
    customized_success_message: "Fetched documents data.",
    customized_failure_message:
      "Unable to fetch your form, please try again. If the problem persists, please contact support and attach a screenshot.",
    customized_error_message:
      "Error occured while fetching your client, please contact support and attach a screenshot.",
    isblob: false,
    forMDM: true,
  });

  if (response.success) {
    setFileInputs(response.data);
  }

  setDocumentTypesApiProgress(false);
};
